import { forwardRef, useEffect, useMemo, useState } from "react";

import AutocompleteSelect from "@/common/components/Autocomplete/AutocompleteSelect";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useDebounce } from "@/common/hooks/useDebounce";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import {
  AutocompleteImplementationProps,
  AutocompleteValueProps,
} from "@/common/utils/autocomplete";
import { translateError } from "@/common/utils/errorHandling";
import { LinearTrackingEntityDisplay } from "@/integration/components/linear/LinearTrackingEntityDisplay";
import { useLinearTrackingEntitySearch } from "@/integration/data/useLinearTrackingEntitySearch";
import { SEARCH_DEBOUNCE_MS } from "@reflag/shared/constants";
import { LinearTrackingEntity } from "@reflag/shared/linearConnectionAPI";

export type LinearTrackingEntityAutocompleteSelectProps =
  AutocompleteImplementationProps<LinearTrackingEntity> &
    AutocompleteValueProps<LinearTrackingEntity>;

export const LinearTrackingEntityAutocompleteSelect = forwardRef<
  HTMLButtonElement,
  LinearTrackingEntityAutocompleteSelectProps
>(function LinearTrackingEntityAutocompleteSelect(
  {
    value,
    placeholder = "Select a linear issue/project",
    canClear,
    onChange,
    ...autocompleteProps
  }: LinearTrackingEntityAutocompleteSelectProps,
  ref,
) {
  const { org } = useEnvironment();
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const errorToast = useErrorToast();
  const {
    data = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useLinearTrackingEntitySearch(debouncedSearchValue);

  const suggestions = useMemo(() => {
    return value ? [value, ...data.filter((e) => e.id !== value.id)] : data;
  }, [data, value]);

  useEffect(() => {
    if (isError) {
      const { title, description } = translateError(
        error,
        `Failed to search for Linear issues/projects`,
      );

      errorToast({
        title,
        description,
        duration: 5000,
      });
    }
  }, [isError, error, errorToast]);

  return (
    <AutocompleteSelect
      ref={ref}
      canClear={canClear}
      isDisabled={isError || !org.integrations.linear}
      isLoading={isLoading}
      isLoadingSuggestions={isFetching}
      itemKeyProperty="id"
      itemRenderFn={(e, search) => (
        <LinearTrackingEntityDisplay
          entity={e}
          py={0.5}
          search={search}
          showIcon={false}
        />
      )}
      loadingText="Loading..."
      placeholder={placeholder}
      suggestions={suggestions}
      value={value ?? undefined}
      onChange={(newValue: LinearTrackingEntity | null) => {
        setSearchValue("");
        if (!newValue) {
          if (canClear) {
            onChange?.(null);
          }
          return;
        }

        onChange?.(newValue ?? null);
      }}
      onClear={() => {
        setSearchValue("");
      }}
      onSearchInput={setSearchValue}
      {...autocompleteProps}
    />
  );
});
